import { useTranslation } from "react-i18next";
import { useContext, useEffect, useState } from "react";
import Measurement from "./Measurement";
import InitContext from "../pages/InitContext";

import {
  faChartLine,
  faSortDown,
  faSortUp,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import useMmcService from "../../services/MmcService";
import ErrorMessage from "../errorMessage/ErrorMessage";
import Spinner from "../spinner/Spinner";
import MeasurementsLineChart from "./MeasurementsLineChart";
import { formatDateTimeForGui, parseDate } from "../pages/utils";
import ReactDOM from "react-dom";
import Select from "react-select";
import {Consts} from "../pages/consts";

const MeasurementsTable = ({
  children,
  selectedPatient,
  hcpView,
  printView = false,
}) => {
  const { t: t } = useTranslation();
  const [measurements, setMeasurements] = useState([]);
  const [addMeasurement, setAddMeasurement] = useState(false);
  const context = useContext(InitContext);

  const { loading, error, clearError, getDbMeasurements, deleteDbMeasurement } =
    useMmcService();

  const [showMeasurementsLineChart, setShowMeasurementsLineChart] =
    useState(false);

  const [sortConfig, setSortConfig] = useState({
    key: Consts.DATE_TIME,
    ascending: false,
  });

  const [sortedMeasurements, setSortedMeasurements] = useState(
    measurements ? [...measurements] : [],
  );

  const sortOptions = [
    { value: Consts.DATE_TIME, label: t("measurement_date") },
    { value: Consts.NAME, label: t("measurement_name") },
  ];

  useEffect(() => {
    const sorted = sortData(sortConfig?.key, measurements, sortConfig?.ascending);
    setSortedMeasurements(sorted);
  }, [measurements, sortConfig]);

  const sortData = (key, measurements, ascending) => {
    if (!measurements || measurements?.length === 0) return [];

    return [...measurements]?.sort((a, b) => {
      const valueA = a[key];
      const valueB = b[key];

      if (valueA === null && valueB === null) return 0;
      if (valueA === null) return ascending ? -1 : 1;
      if (valueB === null) return ascending ? 1 : -1;

      const dateA = parseDate(valueA);
      const dateB = parseDate(valueB);

      if (dateA || dateB) {
        return ascending ? (dateA > dateB ? 1 : -1) : dateA < dateB ? 1 : -1;
      }

      return ascending ? (valueA > valueB ? 1 : -1) : valueA < valueB ? 1 : -1;
    });
  };

  const handleSortChange = (key) => {
    const ascending = sortConfig?.key === key ? !sortConfig?.ascending : true;
    setSortConfig({ key, ascending });
  };

  const patientId = !selectedPatient ? context?.patient?.id : selectedPatient?.id;

  useEffect(() => {
    clearError();

    if (patientId) {
      getDbMeasurements(context?.token, patientId).then((measurements) =>
        handleMeasurementsFromDb(measurements),
      );
    }
  }, [printView, patientId]);

  const handleMeasurementsFromDb = (measurements) => {
    if (measurements) {
      setMeasurements(
        measurements?.filter((measurement) => measurement?.params?.length > 0),
      );
    }
  };

  const handleDeleteMeasurement = (measurement) => {
    clearError();

    const newMeasurements = measurements?.filter(function (loopMeasurement) {
      if (measurement?.id) {
        return loopMeasurement?.id !== measurement?.id;
      } else {
        return (
          loopMeasurement?.dateTime !== measurement?.dateTime &&
          loopMeasurement?.name !== measurement?.name
        );
      }
    });

    deleteDbMeasurement(context?.token, measurement?.id).then(() =>
      setMeasurements(newMeasurements),
    );
  };

  const addNewMeasurement = () => {
    setAddMeasurement(true);
  };

  const handleSaveNewMeasurement = (measurement) => {
    if (measurement) {
      measurement = {
        ...measurement,
        dateTime: formatDateTimeForGui(measurement.dateTime),
      };

      setMeasurements([...measurements, measurement]);
    }

    setAddMeasurement(false);
  };

  const handleCloseMeasurementsLineChat = () => {
    setShowMeasurementsLineChart(false);
  };

  let message =
    error && error?.includes(Consts.DELETE)
      ? t("error_by_deleting_a_measurement")
      : t("error_by_retrieving_measurements");

  const errorMessage = error && <ErrorMessage message={message} />;
  const spinner = !printView && loading && <Spinner />;

  return (
    <>
      {addMeasurement &&
        ReactDOM.createPortal(
          <Measurement
            selectedPatient={selectedPatient}
            onSaveNewMeasurement={handleSaveNewMeasurement}
            showDialog={addMeasurement}
            hcpView={hcpView}
          />,
          document.getElementById(Consts.ROOT),
        )}
      {showMeasurementsLineChart &&
        ReactDOM.createPortal(
          <MeasurementsLineChart
            measurements={measurements}
            onCloseDialog={handleCloseMeasurementsLineChat}
            showDialog={showMeasurementsLineChart}
            patientId={patientId}
          />,
          document.getElementById(Consts.ROOT),
        )}
      {!printView && (
        <>
          <header className="w3-container w3-center w3-padding-small">
            <div className="w3-display-container w3-padding-small">
              {children}
              {measurements?.length > 0 && (
                <div className="w3-display-right">
                  <button
                    className="w3-btn w3-round-xlarge w3-theme-d5"
                    onClick={() => setShowMeasurementsLineChart(true)}
                  >
                    <FontAwesomeIcon icon={faChartLine} />
                  </button>
                </div>
              )}
            </div>
          </header>
          {errorMessage}
          {spinner}
          <div className="w3-row w3-padding-small">
            <div className="w3-col s2">
              {sortedMeasurements?.length > 0 && (
                <label className="w3-right w3-padding-small w3-right">
                  {t("sort_by")}:
                </label>
              )}
            </div>
            <div className="w3-col s6">
              {sortedMeasurements?.length > 0 && (
                <div className="flex-aligned-items">
                  <Select
                    onChange={(selectedOption) =>
                      handleSortChange(selectedOption?.value)
                    }
                    value={sortOptions?.find(
                      (option) => option?.value === sortConfig?.key,
                    )}
                    options={sortOptions}
                    placeholder={t("select")}
                    className="custom-select"
                    classNamePrefix="custom-select"
                  />
                  <FontAwesomeIcon
                    onClick={() => handleSortChange(sortConfig?.key)}
                    icon={sortConfig?.ascending ? faSortUp : faSortDown}
                    className="w3-left w3-padding-small"
                  />
                </div>
              )}
            </div>
            <div className="w3-col s4 w3-right">
              <div className="w3-padding-small">
                <button
                  type="button"
                  onClick={addNewMeasurement}
                  className="w3-btn w3-round-xlarge w3-theme-d5 responsive-button"
                >
                  + {t("add_new_measurement")}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
      <div
        className={
          printView
            ? "w3-container w3-left-align"
            : "w3-container form-content-scroll"
        }
      >
        {sortedMeasurements?.length > 0 ? (
          <div className="w3-container">
            {sortedMeasurements?.map((item, i) => {
              return (
                <div
                  className="w3-row w3-container w3-padding-small w3-margin w3-border w3-border-blue w3-round-large"
                  key={`measurementParam${i}`}
                >
                  <div className="w3-threequarter">
                    <label>
                      {item?.dateTime}: {t(item?.name)}
                    </label>
                    {item?.params?.length > 0 &&
                      item?.params?.map((itemInner, k) => {
                        return (
                          <div key={k}>
                            <label className="bold-label">
                              {t(itemInner?.name)}:
                            </label>
                            <label className="label-margin-left">
                              {itemInner?.value}{" "}
                              {itemInner?.unit && t(itemInner?.unit)}
                            </label>
                          </div>
                        );
                      })}
                  </div>
                  {!printView && (
                    <div className="w3-quarter">
                      <label
                        onClick={() => handleDeleteMeasurement(item)}
                        className="w3-btn w3-round-xlarge w3-theme-d5 w3-right"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </label>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="w3-container w3-center">
            <label>{selectedPatient ? t("not_available") : t("you_have_not_yet_entered_any_measurements")}</label>
          </div>
        )}
      </div>
    </>
  );
};

export default MeasurementsTable;
